import dayjs from 'dayjs';
import cookie from 'js-cookie';
import type { PAGE_POSITIONS } from 'types';
import type { TrackableProduct } from 'types/TrackableProduct';
import type { User } from 'types/User';
import * as uuid from 'uuid';

import algolia from '~/services/algolia.client';
import braze from '~/services/braze.client';
import facebook from '~/services/facebook';
import facebookPixel from '~/services/facebook.pixel';
import gtm from '~/services/gtm.client';
import tiktok from '~/services/tiktok.pixel';
import twitter from '~/services/twitter.pixel';
import { getEntrypoint, getReferrer } from '~/utils/navigationContext';

import UserEvent from './UserEvent';

type EventData = {
  affiliateId: string;
  affiliateName: string;
  buttonPosition: number;
  clientIp: string | null;
  currency: string;
  isBot: boolean;
  isReseller: boolean;
  pagePath: string;
  pagePosition: PAGE_POSITIONS;
  price: number | null;
  product: TrackableProduct;
  productImageUrl?: string;
  productUrl?: string;
  queryID?: string | null;
  user: User;
};

class AffiliateBuyNowEvent extends UserEvent<EventData> {
  private getPrice() {
    return this.data.price ?? 0;
  }

  private getAffiliateName() {
    return this.data.affiliateName;
  }

  private getProductId() {
    return this.data.product.product_id;
  }

  private getProductName() {
    return this.data.product.name ?? '';
  }

  private getCurrencey() {
    return this.data.currency;
  }

  async sendToFacebook() {
    const eventId = uuid.v4();

    await facebookPixel.fireEvent({
      data: {
        affiliate: this.getAffiliateName(),
        content_ids: [this.getProductId()],
        content_type: 'product_group',
        currency: this.getCurrencey(),
        value: this.getPrice()
      },
      eventID: eventId,
      eventName: facebookPixel.StandardEventNames.AddToCart
    });

    const hashedProfile = await facebook.getHashedProfile(
      this.data.user.profile
    );

    const serverEventData = [
      {
        action_source: 'website',
        custom_data: {
          brand: this.data.product?.brand || '',
          content_ids: [this.getProductId()],
          content_name: this.data.product?.name ?? '',
          content_type: 'product_group',
          currency: this.data.product?.price.currency ?? '',
          value: this.getPrice()
        },
        event_id: eventId,
        event_name: facebookPixel.StandardEventNames.AddToCart,
        event_source_url: this.data.pagePath,
        event_time: dayjs().unix(),
        user_data: {
          client_ip_address: this.data.clientIp,
          client_user_agent: navigator?.userAgent,
          external_id: this.data.user.id ?? 'guest',
          fbc: cookie.get('_fbc'),
          fbp: cookie.get('_fbp'),
          ...hashedProfile
        }
      }
    ];

    this.serverEvents.push({ data: serverEventData, type: 'facebook' });
  }

  sendToTikTok() {
    return tiktok.fireEvent('AddToCartAF', {
      affiliate: this.getAffiliateName(),
      content_id: this.getProductId(),
      content_type: 'product',
      currency: this.getCurrencey(),
      value: this.getPrice()
    });
  }

  sendToTwitter() {
    return twitter.fireEvent(twitter.EventIds.AddToCartAF, {
      contents: [{ content_id: this.getProductId(), content_type: 'product' }],
      currency: this.getCurrencey(),
      value: this.getPrice()
    });
  }

  sendToGtm() {
    gtm.pushToDataLayer({
      currency: this.getCurrencey(),
      event: 'retailerLinkClick',
      price: this.getPrice(),
      product_name: this.getProductName(),
      retailer_name: this.getAffiliateName()
    });

    gtm.Ecommerce.addToCart({
      affiliateName: this.getAffiliateName(),
      buttonPosition: this.data.buttonPosition,
      currency: this.getCurrencey(),
      isReseller: this.data.isReseller,
      item: gtm.Ecommerce.getEcommerceItem(this.data.product),
      pagePosition: this.data.pagePosition,
      price: this.getPrice()
    });
  }

  sendToAlgolia() {
    if (!this.data.queryID) return;

    algolia.addedToCartObjectIDsAfterSearch({
      id: this.getProductId()?.toString(),
      queryID: this.data.queryID,
      user: this.data.user
    });
  }

  sendToBraze(): void {
    braze.logAffiliateViewedEvent({
      imageUrl: this.data.productImageUrl,
      productId: this.getProductId(),
      productName: this.data.product?.name ?? '',
      productType: this.data.product?.sub_type,
      productUrl: this.data.productUrl,
      retailerName: this.data.affiliateName
    });
  }

  collectServerEvents() {
    this.serverEvents.push({
      data: {
        client_ip_address: this.data.clientIp ?? '',
        entry_point: getEntrypoint(),
        is_logged_in: this.data.user.isLoggedIn,
        product_id: this.getProductId(),
        referrer: getReferrer(),
        retailer_id: this.data.affiliateId,
        retailer_name: this.getAffiliateName()
      },
      type: 'retailer_click'
    });
  }
}

export default AffiliateBuyNowEvent;
